import { Density, InputType, Size } from '@philips-emr/tws-constants';
import { AwesomeEvent, ComponentAPI } from '@philips-emr/tws-core';
import Changeable, { ChangeEventListener } from '@philips-emr/tws-core/lib/component/types/changeable';
import Focusable, { FocusEventListener } from '@philips-emr/tws-core/lib/component/types/focusable';
import Keyboardable, { KeyboardEventListener } from '@philips-emr/tws-core/lib/component/types/keyboardable';
import { isEnumValid } from '@philips-emr/tws-utils';

class InputAPI extends ComponentAPI implements Changeable<string>, Focusable, Keyboardable {

  /** @event */
  @AwesomeEvent
  onChange = new ChangeEventListener<string>();

  /** @event */
  @AwesomeEvent
  onBlur = new FocusEventListener();

  /** @event */
  @AwesomeEvent
  onFocus = new FocusEventListener();

  /** @event */
  @AwesomeEvent
  onKeyDown = new KeyboardEventListener();

  /** @event */
  @AwesomeEvent
  onKeyPress = new KeyboardEventListener();

  /** @event */
  @AwesomeEvent
  onKeyUp = new KeyboardEventListener();

  isAutoCapitalize(): boolean {
    return this.state.autoCapitalize;
  }

  setAutoCapitalize(autoCapitalize: boolean): void {
    this.setState({ autoCapitalize });
  }

  isAutoComplete(): boolean {
    return this.state.autoComplete;
  }

  setAutoComplete(autoComplete: boolean): void {
    this.setState({ autoComplete });
  }

  isAutoCorrect(): boolean {
    return this.state.autoCorrect;
  }

  setAutoCorrect(autoCorrect: boolean): void {
    this.setState({ autoCorrect });
  }

  isEnabled(): boolean {
    return this.state.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.setState({ enabled });
  }

  isEnforceLength(): boolean {
    return this.state.enforceLength;
  }

  setEnforceLength(enforceLength: boolean): void {
    this.setState({ enforceLength });
  }

  getLength(): number {
    return this.state.length;
  }

  setLength(length: number): void {
    this.setState({ length });
  }

  getMax(): number {
    return this.state.max;
  }

  setMax(max: number): void {
    this.setState({ max });
  }

  getMin(): number {
    return this.state.min;
  }

  setMin(min: number): void {
    this.setState({ min });
  }

  getName(): string {
    return this.state.name;
  }

  setName(name: string): void {
    this.setState({ name });
  }

  getPlaceholder(): string {
    return this.state.placeholder;
  }

  setPlaceholder(placeholder: string): void {
    this.setState({ placeholder });
  }

  isReadOnly(): boolean {
    return this.state.readOnly;
  }

  setReadOnly(readOnly: boolean): void {
    this.setState({ readOnly });
  }

  isRequired(): boolean {
    return this.state.required;
  }

  setRequired(required: boolean): void {
    this.setState({ required });
  }

  isShowLength(): boolean {
    return this.state.showLength;
  }

  setShowLength(showLength: boolean): void {
    this.setState({ showLength });
  }

  isShowPasswordToggle(): boolean {
    return this.state.showPasswordToggle;
  }

  setShowPasswordToggle(showPasswordToggle: boolean): void {
    this.setState({ showPasswordToggle });
  }
  getSize(): Size {
    return this.state.size;
  }

  setSize(size: Size): void {
    if (isEnumValid(Size, size)) {
      this.setState({ size });
    }
  }

  getStep(): number {
    return this.state.step;
  }

  setStep(step: number): void {
    this.setState({ step });
  }

  getType(): InputType {
    return this.state.type;
  }

  setType(type: InputType): void {
    if (isEnumValid(InputType, type)) {
      this.setState({ type });
    }
  }

  getValue(): string {
    return this.state.value;
  }

  setValue(value: string): void {
    const { value: oldValue } = this.state;
    this.setState({ value });

    this.onChange.fire({ value, oldValue });
  }

  setFocus(focused: boolean): void {
    this.setState({ focused });
  }

  getDensity(): Density {
    return this.state.density;
  }

  setDensity(density: Density): void {
    this.setState({ density });
  }

  isAutoFocus(): boolean {
    return this.state.autoFocus;
  }

  setAutoFocus(autoFocus: boolean): void {
    this.setState({ autoFocus });
  }

}

export default InputAPI;
